import { useState, useEffect, useCallback } from 'react'
import { Outlet } from 'react-router-dom'
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Avatar,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Divider,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
  useBreakpointValue,
  useColorMode,
  useColorModeValue,
  Tooltip,
} from '@chakra-ui/react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  ChatTeardropDots,
  ChartBar,
  Folders,
  Flag,
  UsersThree,
  SignOut,
  List,
  ChartPie,
  User,
  CaretLeft,
  CaretRight,
  Robot,
  Brain,
  Chats,
  Lightning,
  GearSix,
  Ticket,
  Sun,
  Moon,
  Tag,
} from '@phosphor-icons/react'
import { useAuth } from '../contexts/AuthContext'
import { PushPermissionPrompt } from '../components/PushPermissionPrompt'
import { PushClickBridge } from '../components/PushClickBridge'
import api from '../services/api'
import socketService, { SOCKET_EVENTS } from '../services/socket'

function NavItem({
  to,
  icon: Icon,
  label,
  badge,
  onClick,
  isCollapsed,
}: {
  to: string
  icon: React.ElementType
  label: string
  badge?: number
  onClick?: () => void
  isCollapsed?: boolean
}) {
  const location = useLocation()
  const isActive = location.pathname.startsWith(to)
  const inactiveColor = useColorModeValue('gray.600', 'gray.300')
  const hoverBg = useColorModeValue('gray.200', 'gray.700')

  const content = (
    <NavLink to={to} onClick={onClick}>
      <HStack
        px={isCollapsed ? 3 : 4}
        py={3}
        borderRadius="lg"
        bg={isActive ? 'yellow.500' : 'transparent'}
        color={isActive ? 'gray.900' : inactiveColor}
        _hover={{ bg: isActive ? 'yellow.400' : hoverBg }}
        transition="all 0.2s"
        justify={isCollapsed ? 'center' : 'space-between'}
      >
        <HStack spacing={isCollapsed ? 0 : 3}>
          <Icon size={20} weight={isActive ? 'fill' : 'regular'} />
          {!isCollapsed && (
            <Text fontWeight={isActive ? 'semibold' : 'normal'}>{label}</Text>
          )}
        </HStack>
        {!isCollapsed && badge !== undefined && badge > 0 && (
          <Flex
            bg="red.500"
            color="white"
            borderRadius="full"
            minW="20px"
            h="20px"
            fontSize="11px"
            fontWeight="bold"
            align="center"
            justify="center"
            px="5px"
            lineHeight="1"
          >
            {badge > 99 ? '99+' : badge}
          </Flex>
        )}
        {isCollapsed && badge !== undefined && badge > 0 && (
          <Flex
            bg="red.500"
            color="white"
            borderRadius="full"
            position="absolute"
            top={1}
            right={1}
            minW="16px"
            h="16px"
            fontSize="10px"
            fontWeight="bold"
            align="center"
            justify="center"
            px="3px"
            lineHeight="1"
          >
            {badge > 9 ? '9+' : badge}
          </Flex>
        )}
      </HStack>
    </NavLink>
  )

  if (isCollapsed) {
    return (
      <Tooltip label={label} placement="right" hasArrow>
        <Box position="relative">{content}</Box>
      </Tooltip>
    )
  }

  return content
}

export function Sidebar({ onClose, isCollapsed, onToggleCollapse, internalTicketsBadge }: { 
  onClose?: () => void
  isCollapsed?: boolean
  onToggleCollapse?: () => void
  internalTicketsBadge?: number
}) {
  const { user, canManageSystem, logout } = useAuth()
  const { colorMode, toggleColorMode } = useColorMode()
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const mutedColor = useColorModeValue('gray.500', 'gray.400')
  const hoverBg = useColorModeValue('gray.200', 'gray.700')
  const titleColor = useColorModeValue('gray.800', 'white')
  const nameColor = useColorModeValue('gray.800', 'white')
  const emailColor = useColorModeValue('gray.500', 'gray.400')
  const iconMutedColor = useColorModeValue('gray.500', 'gray.400')

  const menuItems = [
    { to: '/tickets', icon: ChatTeardropDots, label: 'Atendimentos' },
    { to: '/my-internal-tickets', icon: Ticket, label: 'Meus Tickets', badge: internalTicketsBadge },
    { to: '/internal-ticket-categories', icon: Tag, label: 'Categorias de Tickets' },
    { to: '/quick-replies', icon: Lightning, label: 'Respostas Rápidas' },
    { to: '/my-metrics', icon: ChartPie, label: 'Minhas Métricas' },
  ]

  const adminMenuItems = [
    { to: '/internal-tickets', icon: Ticket, label: 'Tickets Internos' },
    { to: '/all-chats', icon: Chats, label: 'Conversas' },
    { to: '/categories', icon: Folders, label: 'Categorias' },
    { to: '/status', icon: Flag, label: 'Status' },
    { to: '/groups', icon: UsersThree, label: 'Grupos' },
    { to: '/automations', icon: Robot, label: 'Automações' },
    { to: '/ai-agents', icon: Brain, label: 'Agentes de IA' },
    { to: '/metrics', icon: ChartBar, label: 'Métricas' },
    { to: '/settings', icon: GearSix, label: 'Configurações' },
  ]

  return (
    <VStack h="100%" spacing={0} align="stretch">
      {/* Logo */}
      <Box p={isCollapsed ? 2 : 4} borderBottom="1px" borderColor={borderColor}>
        <HStack spacing={isCollapsed ? 0 : 3} justify={isCollapsed ? 'center' : 'flex-start'}>
          <Box
            w={10}
            h={10}
            bg="yellow.500"
            borderRadius="lg"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
          >
            <ChatTeardropDots size={24} color="#1A202C" weight="fill" />
          </Box>
          {!isCollapsed && (
            <VStack align="start" spacing={0}>
              <Text fontWeight="bold" fontSize="lg" color={titleColor}>
                WeDrop
              </Text>
              <Text fontSize="xs" color={mutedColor}>
                Suporte
              </Text>
            </VStack>
          )}
        </HStack>
      </Box>

      {/* Toggle button */}
      {onToggleCollapse && (
        <Box px={2} py={2}>
          <Tooltip label={isCollapsed ? 'Expandir menu' : 'Recolher menu'} placement="right" hasArrow>
            <IconButton
              aria-label={isCollapsed ? 'Expandir menu' : 'Recolher menu'}
              icon={isCollapsed ? <CaretRight size={16} /> : <CaretLeft size={16} />}
              size="sm"
              variant="ghost"
              w="100%"
              onClick={onToggleCollapse}
              color={iconMutedColor}
              _hover={{ bg: hoverBg, color: titleColor }}
            />
          </Tooltip>
        </Box>
      )}

      {/* Navigation */}
      <VStack flex={1} p={isCollapsed ? 2 : 4} spacing={2} align="stretch" overflowY="auto">
        {menuItems.map((item) => (
          <NavItem key={item.to} {...item} onClick={onClose} isCollapsed={isCollapsed} />
        ))}

        {canManageSystem && (
          <>
            <Divider my={2} borderColor={borderColor} />
            {!isCollapsed && (
              <Text fontSize="xs" color={mutedColor} px={4} py={2} textTransform="uppercase">
                Administração
              </Text>
            )}
            {adminMenuItems.map((item) => (
              <NavItem key={item.to} {...item} onClick={onClose} isCollapsed={isCollapsed} />
            ))}
          </>
        )}
      </VStack>

      {/* Theme toggle */}
      <Box px={isCollapsed ? 2 : 4} pb={2}>
        {isCollapsed ? (
          <Tooltip label={colorMode === 'dark' ? 'Modo claro' : 'Modo escuro'} placement="right" hasArrow>
            <IconButton
              aria-label="Alternar tema"
              icon={colorMode === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
              size="sm"
              variant="ghost"
              onClick={toggleColorMode}
              w="100%"
              color={iconMutedColor}
              _hover={{ bg: hoverBg, color: titleColor }}
            />
          </Tooltip>
        ) : (
          <HStack
            px={4}
            py={2}
            borderRadius="lg"
            cursor="pointer"
            onClick={toggleColorMode}
            _hover={{ bg: hoverBg }}
            transition="all 0.2s"
            spacing={3}
          >
            {colorMode === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
            <Text fontSize="sm" color={mutedColor}>
              {colorMode === 'dark' ? 'Modo claro' : 'Modo escuro'}
            </Text>
          </HStack>
        )}
      </Box>

      {/* User */}
      <Box p={isCollapsed ? 2 : 4} borderTop="1px" borderColor={borderColor}>
        <Menu>
          <MenuButton w="100%">
            {isCollapsed ? (
              <Tooltip label={user?.name} placement="right" hasArrow>
                <Flex justify="center">
                  <Avatar
                    size="sm"
                    name={user?.name}
                    src={user?.profilePic || undefined}
                    bg="yellow.500"
                    color="gray.900"
                  />
                </Flex>
              </Tooltip>
            ) : (
              <HStack
                p={2}
                borderRadius="lg"
                _hover={{ bg: hoverBg }}
                transition="all 0.2s"
              >
                <Avatar
                  size="sm"
                  name={user?.name}
                  src={user?.profilePic || undefined}
                  bg="yellow.500"
                  color="gray.900"
                />
                <VStack align="start" spacing={0} flex={1}>
                  <Text fontSize="sm" fontWeight="medium" color={nameColor} noOfLines={1}>
                    {user?.name?.split(' ').slice(0, 2).join(' ')}
                  </Text>
                  <Text fontSize="xs" color={emailColor} noOfLines={1}>
                    {user?.email}
                  </Text>
                </VStack>
              </HStack>
            )}
          </MenuButton>
          <MenuList>
            <MenuItem icon={<User size={18} />}>Meu Perfil</MenuItem>
            <MenuItem icon={<SignOut size={18} />} onClick={logout} color="red.400">
              Sair
            </MenuItem>
          </MenuList>
        </Menu>
      </Box>
    </VStack>
  )
}

/**
 * Barra inferior do celular (estilo app de mensagens). Some quando uma conversa
 * ou ticket está aberto: a conversa ocupa a tela inteira, como no WhatsApp.
 */
function MobileTabBar({ internalTicketsBadge, onOpenMenu }: { internalTicketsBadge: number; onOpenMenu: () => void }) {
  const location = useLocation()
  const surfaceBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const inactive = useColorModeValue('gray.500', 'gray.400')

  const tabs = [
    { to: '/tickets', icon: ChatTeardropDots, label: 'Conversas' },
    { to: '/my-internal-tickets', icon: Ticket, label: 'Tickets', badge: internalTicketsBadge },
    { to: '/quick-replies', icon: Lightning, label: 'Respostas' },
    { to: '/my-metrics', icon: ChartPie, label: 'Métricas' },
  ]

  return (
    <Flex
      as="nav"
      flexShrink={0}
      bg={surfaceBg}
      borderTop="1px"
      borderColor={borderColor}
      justify="space-around"
      align="stretch"
      pb="env(safe-area-inset-bottom)"
    >
      {tabs.map(({ to, icon: Icon, label, badge }) => {
        const active = location.pathname.startsWith(to)
        return (
          <NavLink key={to} to={to} style={{ flex: 1 }}>
            <VStack spacing={0.5} py={2} color={active ? 'yellow.500' : inactive} position="relative">
              <Box position="relative">
                <Icon size={24} weight={active ? 'fill' : 'regular'} />
                {!!badge && badge > 0 && (
                  <Flex
                    position="absolute"
                    top="-4px"
                    right="-10px"
                    bg="red.500"
                    color="white"
                    borderRadius="full"
                    minW="16px"
                    h="16px"
                    fontSize="10px"
                    fontWeight="bold"
                    align="center"
                    justify="center"
                    px="4px"
                  >
                    {badge > 99 ? '99+' : badge}
                  </Flex>
                )}
              </Box>
              <Text fontSize="10px" fontWeight={active ? 'semibold' : 'normal'}>
                {label}
              </Text>
            </VStack>
          </NavLink>
        )
      })}
      <Box as="button" flex={1} onClick={onOpenMenu} aria-label="Mais opções">
        <VStack spacing={0.5} py={2} color={inactive}>
          <List size={24} />
          <Text fontSize="10px">Mais</Text>
        </VStack>
      </Box>
    </Flex>
  )
}

/** Rotas em que uma conversa/ticket está aberto: tela cheia, sem barra inferior. */
function isDetailRoute(pathname: string) {
  return /^\/(tickets|all-chats|internal-tickets|my-internal-tickets)\/[^/]+/.test(pathname)
}

export default function DefaultLayout() {
  const location = useLocation()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const isMobile = useBreakpointValue({ base: true, lg: false })
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [internalTicketsBadge, setInternalTicketsBadge] = useState(0)
  const surfaceBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const pageBg = useColorModeValue('gray.50', 'gray.900')

  const showTabBar = isMobile && !isDetailRoute(location.pathname)

  // As telas de lista abrem o menu pelo próprio cabeçalho disparando este evento.
  useEffect(() => {
    const abrir = () => onOpen()
    window.addEventListener('open-app-menu', abrir)
    return () => window.removeEventListener('open-app-menu', abrir)
  }, [onOpen])

  const fetchInternalTicketsCount = useCallback(async () => {
    try {
      const res = await api.get('/internal-tickets/my-pending-count')
      setInternalTicketsBadge(res.data.count || 0)
    } catch {
      // ignore
    }
  }, [])

  useEffect(() => {
    fetchInternalTicketsCount()
    const interval = setInterval(fetchInternalTicketsCount, 120000)

    const handleNew = () => fetchInternalTicketsCount()
    const handleReply = () => fetchInternalTicketsCount()
    const handleResolved = () => fetchInternalTicketsCount()

    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.NEW, handleNew)
    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.REPLY, handleReply)
    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.RESOLVED, handleResolved)

    return () => {
      clearInterval(interval)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.NEW, handleNew)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.REPLY, handleReply)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.RESOLVED, handleResolved)
    }
  }, [fetchInternalTicketsCount])

  const toggleCollapse = () => setIsCollapsed(!isCollapsed)

  return (
    <Flex
      h="100%"
      w="100%"
      overflow="hidden"
      direction={{ base: 'column', lg: 'row' }}
      // Status bar do iPhone (app instalado com black-translucent)
      pt="env(safe-area-inset-top)"
      bg={pageBg}
    >
      {/* Drawer do menu completo (celular: botão "Mais" ou ícone no cabeçalho) */}
      <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent bg={surfaceBg} maxW="300px" pt="env(safe-area-inset-top)" pb="env(safe-area-inset-bottom)">
          <DrawerCloseButton />
          <DrawerBody p={0}>
            <Sidebar onClose={onClose} internalTicketsBadge={internalTicketsBadge} />
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Desktop sidebar */}
      {!isMobile && (
        <Box
          w={isCollapsed ? '70px' : '280px'}
          minW={isCollapsed ? '70px' : '280px'}
          bg={surfaceBg}
          borderRight="1px"
          borderColor={borderColor}
          transition="all 0.2s ease-in-out"
        >
          <Sidebar isCollapsed={isCollapsed} onToggleCollapse={toggleCollapse} internalTicketsBadge={internalTicketsBadge} />
        </Box>
      )}

      {/* Main content: as telas de conversa controlam o próprio scroll (h=100%);
          as demais (métricas, configurações...) rolam aqui. */}
      <Box
        flex={1}
        minH={0}
        minW={0}
        overflowY="auto"
        overflowX="hidden"
        bg={pageBg}
        sx={{ overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}
      >
        <Outlet />
        <PushPermissionPrompt />
        <PushClickBridge />
      </Box>

      {showTabBar && <MobileTabBar internalTicketsBadge={internalTicketsBadge} onOpenMenu={onOpen} />}
    </Flex>
  )
}
